import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown, Phone } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+62', country: 'Indonesia', flag: 'ID' },
  { code: '+1', country: 'USA / Canada', flag: 'US' },
  { code: '+61', country: 'Australia', flag: 'AU' },
  { code: '+82', country: 'South Korea', flag: 'KR' },
  { code: '+886', country: 'Taiwan', flag: 'TW' },
  { code: '+86', country: 'China', flag: 'CN' },
  { code: '+65', country: 'Singapore', flag: 'SG' },
  { code: '+60', country: 'Malaysia', flag: 'MY' },
  { code: '+81', country: 'Japan', flag: 'JP' },
  { code: '+66', country: 'Thailand', flag: 'TH' },
  { code: '+84', country: 'Vietnam', flag: 'VN' },
  { code: '+852', country: 'Hong Kong', flag: 'HK' },
  { code: '+91', country: 'India', flag: 'IN' },
  { code: '+44', country: 'United Kingdom', flag: 'GB' },
  { code: '+49', country: 'Germany', flag: 'DE' },
  { code: '+31', country: 'Netherlands', flag: 'NL' },
  { code: '+971', country: 'UAE', flag: 'AE' },
  { code: '+966', country: 'Saudi Arabia', flag: 'SA' },
  { code: '+974', country: 'Qatar', flag: 'QA' },
  { code: '+64', country: 'New Zealand', flag: 'NZ' },
  { code: '+33', country: 'France', flag: 'FR' },
  { code: '+39', country: 'Italy', flag: 'IT' },
  { code: '+34', country: 'Spain', flag: 'ES' },
  { code: '+55', country: 'Brazil', flag: 'BR' },
  { code: '+63', country: 'Philippines', flag: 'PH' },
];

export function WhatsAppInput({ value, onChange, lang = 'en' }) {
  const [open, setOpen] = useState(false);

  // Parse existing value: try to detect country code
  const detectCode = () => {
    if (!value) return { cc: '+62', num: '' };
    const v = value.replace(/\s/g, '');
    for (const c of COUNTRY_CODES) {
      if (v.startsWith(c.code)) return { cc: c.code, num: v.slice(c.code.length) };
    }
    // If starts with digits only, assume +62
    return { cc: '+62', num: v.replace(/^\+/, '') };
  };

  const { cc, num } = detectCode();

  const handleCodeChange = (newCode) => {
    onChange(newCode + num);
    setOpen(false);
  };

  const handleNumChange = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '');
    // Strip leading 0
    if (v.startsWith('0')) v = v.slice(1);
    // Strip leading country-code digits if the user typed/pasted the country code as well
    // (e.g. user types '6281234567890' while dropdown is already +62 → keep '81234567890')
    const ccDigits = cc.replace(/^\+/, '');
    if (ccDigits && v.startsWith(ccDigits)) {
      v = v.slice(ccDigits.length);
      // handle "+62 0812..." style paste after cc-strip
      if (v.startsWith('0')) v = v.slice(1);
    }
    onChange(cc + v);
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === cc) || COUNTRY_CODES[0];

  return (
    <div>
      <div className="flex gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="wa-country-code-trigger"
              className="flex items-center gap-1 t-bg-input t-border h-10 rounded-lg px-2.5 text-sm t-text min-w-[80px] justify-between flex-shrink-0">
              <span className="font-medium">{cc}</span>
              <ChevronsUpDown className="w-3 h-3 t-text-muted" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-0 glass-card" align="start">
            <Command className="bg-transparent">
              <CommandInput placeholder="Search country..." className="t-text" />
              <CommandList className="max-h-[200px]">
                <CommandEmpty className="t-text-muted text-sm py-3 text-center">Not found</CommandEmpty>
                <CommandGroup>
                  {COUNTRY_CODES.map(c => (
                    <CommandItem key={c.code} value={`${c.country} ${c.code}`} onSelect={() => handleCodeChange(c.code)} className="t-text text-sm">
                      <Check className={`w-3.5 h-3.5 mr-2 ${cc === c.code ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="mr-2 font-medium">{c.code}</span>
                      <span className="t-text-muted text-xs">{c.country}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="relative flex-1">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 t-text-muted" />
          <input type="tel" value={num} onChange={handleNumChange} data-testid="wa-phone-input"
            className="w-full t-bg-input t-border h-10 rounded-lg text-sm pl-9 pr-3 t-text outline-none focus:ring-1 focus:ring-[#D98A94]/30"
            placeholder={lang === 'en' ? 'Phone number (no leading 0)' : 'Nomor HP (tanpa awalan 0)'} />
        </div>
      </div>
      <p className="t-text-muted text-[10px] mt-1 pl-1">
        {lang === 'en' ? 'Do not start with 0. Replace the 0 with the country code above.' : 'Jangan awali dengan 0. Ganti 0 dengan kode negara di atas.'}
      </p>
    </div>
  );
}
